import { groupBy, partition } from 'lodash';
import { WidgetInputType } from '../input/WidgetInputType';
import { getWidgetSubtype, IWidget, WidgetSubtype } from '../Widget';
import { WidgetDisposal } from './WidgetDisposal';

export class WidgetDisposalLogic {
  /**
   * Controlla se un Widget è inseribile nel modulo corrente.
   * Alcuni Widget sono inseribili soltanto una volta per form, come
   * indicato dall'enum {@link WidgetDisposal}
   */
  static canAddToForm(formWidgets: IWidget[], type: WidgetSubtype) {
    if (this.isUnique(type)) {
      return !formWidgets.some(w => w.input?.type === type);
    }

    // Di default si possono aggiungere tutti
    return true;
  }

  /**
   * Ottiene il tipo di disposizione possibile nel form
   */
  static getDisposal(type: WidgetSubtype | undefined, widget?: IWidget) {
    switch (type) {
      case WidgetInputType.UploadSigned:
      case WidgetInputType.Preview:
        return this.getForcedDisposal(WidgetDisposal.Step, widget);

      case WidgetInputType.Captcha:
        return this.getForcedDisposal(WidgetDisposal.Fixed, widget);

      default:
        return WidgetDisposal.Free;
    }
  }

  /**
   * Ritorna la disposal richiesta a meno che non sia forzata la disposal
   * libera nelle impostazioni del widget.
   */
  private static getForcedDisposal(
    defaultDisposal: WidgetDisposal,
    widget?: IWidget
  ) {
    return widget?.input!.extra?.forceFreeDisposal
      ? WidgetDisposal.Free
      : defaultDisposal;
  }

  /**
   * Ritorna vero se il tipo di widget è utilizzabile un unica
   * volta per procedure (e.g. captcha)
   */
  static isUnique(type: WidgetSubtype) {
    switch (type) {
      case WidgetInputType.Captcha:
        return true;
      default:
        return false;
    }
  }

  static getWidgetDisposal(widget: IWidget) {
    return this.getDisposal(getWidgetSubtype(widget), widget);
  }

  /**
   * Controlla se un tipo di Widget è "libero", ovvero liberamente ordinabile
   */
  static hasFreeDisposal(type: WidgetSubtype | undefined) {
    return this.getDisposal(type) === WidgetDisposal.Free;
  }

  /**
   * Divide i Widget in quelli "fissi" e quelli liberi, ovvero liberamente
   * riordinabili dall'utente.
   */
  static split(widgets: IWidget[]): { [key in WidgetDisposal]: IWidget[] } {
    const filterByDisposal = (disposal: WidgetDisposal) => (w: IWidget) =>
      this.getDisposal(getWidgetSubtype(w), w) === disposal;

    return {
      [WidgetDisposal.Free]: widgets.filter(
        filterByDisposal(WidgetDisposal.Free)
      ),
      [WidgetDisposal.Fixed]: widgets.filter(
        filterByDisposal(WidgetDisposal.Fixed)
      ),
      [WidgetDisposal.Step]: widgets.filter(
        filterByDisposal(WidgetDisposal.Step)
      )
    };
  }
}
